import { Component } from '@theme/component';
import { deepMerge } from '@theme/_ibd-utilities';
import { ThemeEvents } from '@theme/events';
import { IbdEvents } from '@theme/_ibd-events';
import { REVIEWS_WIDGET_DEFAULT_SETTINGS, REVIEWS_WIDGET_SCRIPT_URL } from '@theme/_ibd-reviewsio-settings';

/** @typedef {import('@theme/events').VariantUpdateEvent} VariantUpdateEvent */
/** @typedef {import('@theme/_ibd-events').IbdProductUpdateEvent} IbdProductUpdateEvent */
/** @typedef {import('@theme/_ibd-reviewsio-types').ReviewsWidgetSettings} ReviewsWidgetSettings */

/**
 * IBD Product Reviews Component
 *
 * Displays Reviews.io rating snippets for products.
 *
 */
class IbdProductReviews extends Component {
  /** @type {number} */
  maxAttempts = 50;

  /** @type {number} */
  attempts = 0;

  /** @type {number|null} */
  timeout = null;

  /** @type {ReviewsWidgetSettings} */
  settings = REVIEWS_WIDGET_DEFAULT_SETTINGS;

  /** @type {((event: IbdProductUpdateEvent | VariantUpdateEvent) => void)|null} */
  #boundHandleVariantUpdate = null;

  constructor() {
    super();
    this.#injectScript();
  }

  connectedCallback() {
    super.connectedCallback();

    this.#reset();
    this.#loadSettings();
    this.#loadReviewsSnippet();
    this.#bindEventListeners();
  }

  /**
   * Called when the element is updated via morph.
   * Refreshes the skeleton reference in case it was replaced.
   */
  updatedCallback() {
    super.updatedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reset();
    this.#unbindEventListeners();
  }

  #loadSettings() {
    /** @type {ReviewsWidgetSettings|undefined} */
    let settings;

    try {
      const customSettings = JSON.parse(this.dataset.settings ?? '{}');
      settings = deepMerge(this.settings, customSettings);
    } catch (error) {
      console.error('IBD | Product Reviews | #loadSettings | Error parsing settings', error);
      settings = this.settings;
    }

    if (!settings || !('store' in settings) || !settings.store) {
      throw new Error('IBD | Product Reviews | #loadSettings | No store found in settings');
    }

    this.settings = settings;
  }

  #injectScript() {
    const hasScript = document.querySelector(`script[src="${REVIEWS_WIDGET_SCRIPT_URL}"]`) !== null;
    if (hasScript) {
      return;
    }

    const script = document.createElement('script');
    script.src = REVIEWS_WIDGET_SCRIPT_URL;
    script.defer = true;
    document.head.appendChild(script);
  }

  #loadReviewsSnippet() {
    this.attempts++;

    // Check for ReviewsWidget class (loaded from external script)
    // @ts-ignore - ReviewsWidget is loaded from external script
    const ReviewsWidget = window.ReviewsWidget || (typeof ReviewsWidget !== 'undefined' ? ReviewsWidget : null);

    if (typeof ReviewsWidget === 'function') {
      if (this.timeout) clearTimeout(this.timeout);
      new ReviewsWidget('#ReviewsWidget', this.settings);
    } else if (this.attempts < this.maxAttempts) {
      this.timeout = setTimeout(() => this.#loadReviewsSnippet(), 200);
    } else {
      console.error(`IBD | Product Reviews | #loadReviewsSnippet | Failed to load after ${this.maxAttempts} attempts`);
    }
  }

  #reset() {
    this.settings = REVIEWS_WIDGET_DEFAULT_SETTINGS;
    this.attempts = 0;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  /**
   * Handles a variant update event.
   *
   * @param {IbdProductUpdateEvent | VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate(event) {
    if (!event.detail.data || !event.detail.data.html) {
      console.error('IBD | Product Reviews | #handleVariantUpdate | No data or HTML found in event detail');
      return;
    }

    /** @type {HTMLElement|null} */
    const newElement = event.detail.data.html.querySelector('ibd-product-reviews');
    if (!newElement) {
      console.error('IBD | Product Reviews | #handleVariantUpdate | Reviews element not found in html data');
      return;
    }

    this.#loadSettings();
    this.#loadReviewsSnippet();
  }

  #bindEventListeners() {
    this.#boundHandleVariantUpdate = (event) => this.#handleVariantUpdate(event);

    const closestSection = this.closest('.shopify-section, dialog, product-card');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#boundHandleVariantUpdate);
    document.addEventListener(IbdEvents.ibdProductUpdated, this.#boundHandleVariantUpdate);
  }

  #unbindEventListeners() {
    if (this.#boundHandleVariantUpdate) {
      const closestSection = this.closest('.shopify-section, dialog, product-card');
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#boundHandleVariantUpdate);
      document.removeEventListener(IbdEvents.ibdProductUpdated, this.#boundHandleVariantUpdate);

      this.#boundHandleVariantUpdate = null;
    }
  }
}

if (!customElements.get('ibd-product-reviews')) {
  customElements.define('ibd-product-reviews', IbdProductReviews);
}
